import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useSelectedClient } from "../../contexts/SelectedClientContext";
import { useTimer } from "../../contexts/TimerContext";
import { useTheme } from "../../contexts/ThemeContext";
import { DualClock } from "../ui/dual-clock";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Play,
  Square,
  Sun,
  Moon,
  Monitor,
  User,
  LogOut,
  Settings,
  Menu,
} from "lucide-react";
import { cn } from "../../lib/utils";

export const Header = ({ onMobileMenuToggle }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { selectedClient } = useSelectedClient();
  const { isRunning, formatElapsed, stopTimer, activeTimer, activeTimers } = useTimer();
  const { theme, setTheme, isDark } = useTheme();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const themeIcon = theme === "system" ? Monitor : isDark ? Moon : Sun;
  const ThemeIcon = themeIcon;

  return (
    <header className="app-header" data-testid="app-header">
      {/* Mobile hamburger */}
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden mr-2 flex-shrink-0"
        onClick={onMobileMenuToggle}
        aria-label="Open navigation menu"
        data-testid="mobile-menu-btn"
      >
        <Menu className="h-5 w-5" />
      </Button>

      <div className="flex-1" />

      {/* Dual Clock - Shows user timezone and selected client timezone */}
      {user?.default_timezone && (
        <div className="hidden lg:block mr-4">
          <DualClock 
            userTimezone={user.default_timezone} 
            clientTimezone={selectedClient?.client_timezone}
            clientName={selectedClient?.name}
          />
        </div>
      )}

      {/* Timer Widget */}
      <div className="flex items-center gap-2 sm:gap-4">
        {isRunning && (
          <div
            className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 bg-brand-accent/10 rounded-lg border border-brand-accent/20"
            data-testid="active-timer-widget"
          >
            <div className="status-dot status-dot-active" />
            {activeTimers.length === 1 ? (
              <>
                <div>
                  <p className="text-xs text-muted-foreground hidden sm:block">Timer Running</p>
                  <p className="font-mono text-sm sm:text-lg font-semibold text-brand-accent">
                    {formatElapsed()}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => stopTimer(activeTimer.client_id)}
                  className="h-8 w-8 text-brand-accent hover:bg-brand-accent/20"
                  data-testid="stop-timer-btn"
                >
                  <Square className="h-4 w-4 fill-current" />
                </Button>
              </>
            ) : (
              <div
                className="cursor-pointer"
                onClick={() => navigate("/time")}
              >
                <p className="text-xs text-muted-foreground hidden sm:block">Timers Running</p>
                <p className="font-mono text-sm sm:text-lg font-semibold text-brand-accent">
                  {activeTimers.length} active
                </p>
              </div>
            )}
          </div>
        )}

        {!isRunning && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/time")}
            className="gap-2 hidden sm:flex"
            data-testid="start-timer-header-btn"
          >
            <Play className="h-4 w-4" />
            Start Timer
          </Button>
        )}

        {/* Theme Toggle */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="theme-toggle">
              <ThemeIcon className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setTheme("light")}>
              <Sun className="h-4 w-4 mr-2" />
              Light
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("dark")}>
              <Moon className="h-4 w-4 mr-2" />
              Dark
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("system")}>
              <Monitor className="h-4 w-4 mr-2" />
              System
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="gap-2 px-2"
              data-testid="user-menu-trigger"
            >
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="hidden md:inline text-sm font-medium">
                {user?.name || "User"}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings")}>
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
